import { Controller } from "@hotwired/stimulus"

// Instant filtering for the directory.
//
// The form still works without JavaScript — it has a real submit button and a real
// action. This removes the need to press it: typing or picking a facet submits into
// the Turbo frame, which advances history so the URL stays shareable.
//
// The search field lives inside the frame, so that picking a facet re-renders it
// with the current filters attached. That means the field is destroyed on every
// submit, so focus and caret are captured before and restored after.
export default class extends Controller {
  static targets = ["form", "query", "frame"]
  static values = { delay: { type: Number, default: 250 } }

  connect() {
    this.onFrameRender = this.onFrameRender.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    this.element.addEventListener("turbo:frame-render", this.onFrameRender)
    document.addEventListener("keydown", this.onKeydown)
    this.hideFallback()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-render", this.onFrameRender)
    document.removeEventListener("keydown", this.onKeydown)
    clearTimeout(this.timer)
  }

  // Typing waits for a pause; picking a facet applies at once.
  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.submit(), this.delayValue)
  }

  submit() {
    clearTimeout(this.timer)
    if (!this.hasFormTarget) return

    this.caret = this.hasQueryTarget ? this.queryTarget.selectionStart : null
    this.refocus = document.activeElement === this.queryTarget
    this.formTarget.requestSubmit()
  }

  onFrameRender() {
    this.hideFallback()
    if (!this.refocus || !this.hasQueryTarget) return

    const input = this.queryTarget
    input.focus()
    const at = this.caret ?? input.value.length
    input.setSelectionRange(at, at)
  }

  // The submit button is only needed when this controller is not running.
  hideFallback() {
    this.element.querySelectorAll("[data-filter-fallback]").forEach((el) => (el.hidden = true))
  }

  // "/" focuses search, the convention for anything with a lot to look through.
  onKeydown(event) {
    if (event.key !== "/" || event.metaKey || event.ctrlKey || !this.hasQueryTarget) return
    const tag = document.activeElement?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return

    event.preventDefault()
    this.queryTarget.focus()
    this.queryTarget.select()
  }
}
